<template>
    <div class="stylist-dashboard-wrapper">
        <StylistNav />
        <header class="dashboard-welcome-header">
            <div class="container welcome-content-box">
                <h1 class="welcome-title">
                    Welcome, <span class="stylist-name-display">{{ stylistData.username || 'Stylist' }}</span>!
                </h1>
                <p class="welcome-subtitle">Your royal command center for calendar management.</p>
            </div>
        </header>
        <div class="dashboard-content-container container">
            <section class="stylist-profile-section card-style">
                <div class="profile-left">
                    <div class="stylist-picture-placeholder">
                        <span> {{stylistInitials}} </span>
                    </div>
                </div>
                <div class="profile-right">
                    <h2 class="stylist-name">{{ stylistData.username || 'Stylist' }}</h2>
                    <p class="stylist-email"> {{ stylistData.email || 'stylist@salon.com' }} </p> 
                </div>
            </section>
            <div class="afrochic-summary-row">
                <section class="summary-card appointments-card card-style">
                    <h3 class="mini-section-title">✨ Upcoming Appointments</h3>
                    <div class="scrollable-list">
                        <div v-if="loadingAppointments" class="empty-state">Loading your appointments.</div>
                        <div v-else-if="activeAppointments.length === 0" class="empty-state">No appointments found.</div>
                        <ul v-else class="afro-list">
                            <li v-for="appointment in activeAppointments" :key="appointment.appointment_id" class="afro-list-item appointment-item">
                                <div class="info-details">
                                    <span class="appointment-date-time">{{ formatDateTime(appointment.start_time) }}</span>
                                    <span class="appointment-service"><strong>Service:</strong> {{ appointment.service_name }}</span>
                                </div>
                                <div class="client-info">Client: {{ appointment.client_name }}</div>
                            </li>
                        </ul>
                    </div>
                </section>

                <section class="summary-card slots-card card-style">
                    <h3 class="mini-section-title">🌿 Current Active Slots</h3>
                    <div class="scrollable-list">
                        <div v-if="loadingSlots" class="empty-state">Loading your Slots.</div>
                        <div v-else-if="activeSlots.length === 0" class="empty-state">No Slots found.</div>
                        <ul class="afro-list">
                            <li v-for="(slot, index) in activeSlots" :key="index" class="afro-list-item">
                               
                                <span class="slot-time">{{ formatTime(slot.start_time) }} - {{ formatTime(slot.end_time) }}</span>
                                <span v-if="slot.days" class="slot-days">{{ slot.days.join(', ') }}</span>
                                
                                <span v-else class="slot-date">{{ formatDateOnly(slot.date) }}</span>
                            </li>
                        </ul>
                    </div>
                </section>
            </div>

            <section class="timeslot-selection-section card-style">
                <h3 class="section-title">🗓️ Master Availability Calendar</h3>
                <div class="dashboard-grid">
                    <div class="choose-time-slot">
                        <ManageServiceSlots
                            v-if="stylistData.id"
                            :stylist-id="stylistData.id"
                            @refresh-slots="fetchActiveSlots"
                        />
                    </div>
                </div>
            </section>
        </div>
    </div>
</template>

<script>
import axiosInstance from '@/services/AxiosClient.js'
import StylistNav from '@/components/stylist/StylistNav.vue'
import ManageServiceSlots from '@/components/stylist/ManageServiceSlots.vue'

const STYLIST_STORAGE_KEY = 'currentStylist';
const API_BASE_URL = 'https://api-afrobraids.inchtechs.com/api';

export default {
    name: 'StylistDashboard',
    components: { StylistNav, ManageServiceSlots },
    data() {
        return {
            stylistData: {},
            activeSlots: [],
            activeAppointments: [],
            loadingAppointments: true,
            loadingSlots: true,
        }
    },
    async created() {
        // redirect the user(stylist) to google
        this.loadStylistData();
        const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('google_auth') === 'success') {
        this.finalizeGoogleAuth();
    }
        
        // Only fetch if we have a valid stylist ID
        if (this.stylistData.id) {
            this.fetchActiveAppointments();
            this.fetchActiveSlots();
        }
        
    },
    methods: {
        finalizeGoogleAuth() {
        // 1. Update local state
        this.stylistData.google_auth_required = false;
        
        // 2. Update LocalStorage permanently
        ['salon_auth', 'currentStylist'].forEach(key => {
            let data = JSON.parse(localStorage.getItem(key));
            if (data && data.loginResponse) {
                data.loginResponse.google_auth_required = false;
                localStorage.setItem(key, JSON.stringify(data));
            }
        });

        // 3. Clean the URL (removes ?google_auth=success)
        this.$router.replace({ query: null });
    },
        async fetchActiveSlots() {
            this.loadingSlots = true;
            try {
                const res = await axiosInstance.get(`${API_BASE_URL}/slot/get_stylist_slots/${this.stylistData.id}`);
                console.log("Slots data from API:", res.data);
                this.activeSlots = res.data;
            } catch (e) { console.error("Slots error:", e); } finally {
            this.loadingSlots = false;
        }
        },
        async fetchActiveAppointments() {
            this.loadingAppointments = true;
            try {
                const res = await axiosInstance.get(`${API_BASE_URL}/appointment/get`);
                // Filter appointments by current stylist name
                this.activeAppointments = res.data.filter(app => {
                    return app.stylist_name?.toLowerCase() === this.stylistData.username?.toLowerCase();
                }).sort((a, b) => new Date(b.start_time) - new Date(a.start_time));
            } catch (err) { console.error("Appts error:", err); } finally {
            this.loadingAppointments = false;
        }
        },
        formatDateTime(iso) {
            if (!iso) return 'N/A';
            // JavaScript's 'new Date()' automatically converts UTC strings to Local Time
            const date = new Date(iso);
            return date.toLocaleString('en-US', { 
                month: 'short', 
                day: 'numeric', 
                hour: '2-digit', 
                minute: '2-digit' 
            });
        },
        handleNewSlot(newSlot) {
            this.activeSlots.unshift(newSlot);
        },
        loadStylistData() {
            const data = localStorage.getItem(STYLIST_STORAGE_KEY);
            if (data) {
                const user = JSON.parse(data);
                this.stylistData = {
                    id: user.id,
                    username: user.username,
                    email: user.email,
                    access_token: user.access_token,
                    google_auth_required: user.loginResponse?.google_auth_required,
                    google_auth_url: user.loginResponse?.google_auth_url
                };
            }
        },
        formatDateOnly(dateString) {
        if (!dateString) return 'N/A';
        // This takes "Tue, 06 Jan 2026 00:00:00 GMT" and returns "Jan 6, 2026"
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            weekday: 'short',
            month: 'short', 
            day: 'numeric',
            year: 'numeric'
        });
        },

       formatTime(timeString) {
    if (!timeString) return '';
    
    // If backend sends a full ISO string (Recommended)
    if (timeString.includes('T')) {
        return new Date(timeString).toLocaleTimeString([], { 
            hour: 'numeric', 
            minute: '2-digit' 
        });
    }
    
    // Fallback if backend only sends "HH:MM:SS" (Treat as UTC)
    const date = new Date(`1970-01-01T${timeString}Z`); // Notice the 'Z' added
    return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}
    },
    computed: {
        stylistInitials() { return this.stylistData.username ? this.stylistData.username.charAt(0).toUpperCase() : 'S'; }
    }
    /**
     * {
    "id": 3,
    "username": "Lobga Dobga",
    "email": "jamesdobga@gmail.com",
    "access_token": "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJmcmVzaCI6ZmFsc2UsImlhdCI6MTc2ODE1NDgwMywianRpIjoiNGRmZjgzMDYtODFiOS00ZDE4LTg0MDAtM2MwMWZkMDUwNjVmIiwidHlwZSI6ImFjY2VzcyIsInN1YiI6IntcImlkXCI6IDMsIFwicm9sZVwiOiBcInN0eWxpc3RcIn0iLCJuYmYiOjE3NjgxNTQ4MDMsImNzcmYiOiJkZjhiMWEwNy1mMjdhLTRhZjItYWJiZS02MDcwNTQ0YjE0YzEiLCJleHAiOjE3NjgxNTg0MDN9.HU1U2rXAxbrCkOFMpZLYHYDzsXkqvP_Wf7it5Sw7wRE",
    "google_auth_required": true,
    "google_auth_url": "https://api-afrobraids.inchtechs.com/api/auth/google/login"
}
     */
}
</script>

<style scoped>
.afrochic-summary-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 25px;
    margin-bottom: 30px;
}

.summary-card {
    height: 350px;
    display: flex;
    flex-direction: column;
}

.mini-section-title {
    font-family: var(--font-serif);
    color: var(--color-maroon);
    font-size: 1.4rem;
    margin-bottom: 15px;
    padding-bottom: 8px;
    border-bottom: 2px solid var(--color-gold);
}

.scrollable-list {
    flex: 1;
    overflow-y: auto;
    padding-right: 10px;
}

/* Custom Scrollbar for Afrochic feel */
.scrollable-list::-webkit-scrollbar {
    width: 6px;
}
.scrollable-list::-webkit-scrollbar-thumb {
    background: var(--color-earth);
    border-radius: 10px;
}

/* Afrochic List Styling */
.afro-list {
    list-style: none;
    padding: 0;
}

.afro-list-item {
    background: var(--color-light);
    margin-bottom: 10px;
    padding: 12px 15px;
    border-radius: 8px;
    border-left: 4px solid var(--color-earth);
    display: flex;
    justify-content: space-between;
    align-items: center;
    transition: transform 0.2s;
}

.afro-list-item:hover {
    transform: translateX(5px);
    background: #fff;
    box-shadow: 0 4px 8px rgba(0,0,0,0.05);
}

.slot-time {
    font-weight: bold;
    color: var(--color-dark);
}

.slot-days {
    font-size: 0.85rem;
    color: var(--color-maroon);
    text-transform: uppercase;
    letter-spacing: 1px;
}

.empty-state {
    text-align: center;
    color: #888;
    margin-top: 50px;
    font-style: italic;
}

/* Response for mobile */
@media (max-width: 850px) {
    .afrochic-summary-row {
        grid-template-columns: 1fr;
    }
}

.stylist-dashboard-wrapper {
    min-height: 100vh;
    background-color: var(--color-light); /* Off-White/Stone Background */
    font-family: var(--font-sans);
}

.dashboard-content-container {
    max-width: 1300px;
    margin: 0 auto;
    padding: 20px;
}
.container {
    max-width: 1300px;
    margin: 0 auto;
    padding: 0 1rem;
}

/* 1. Welcome Header (The 'Welcome Kwame' replacement) */
.dashboard-welcome-header {
    background-color: var(--color-dark);
    color: var(--color-light);
    padding: 3rem 0;
    margin-bottom: 30px;
    /* Subtle geometric pattern effect */
    background-image: repeating-linear-gradient(
        45deg,
        rgba(251, 191, 36, 0.05),
        rgba(251, 191, 36, 0.05) 10px,
        transparent 10px,
        transparent 20px
    );
}
.welcome-content-box {
    padding: 0 20px;
}

.welcome-title {
    font-family: var(--font-serif);
    font-size: 3rem;
    font-weight: 700;
    margin-bottom: 0.5rem;
}

.stylist-name-display {
    color: var(--color-gold);
}

.welcome-subtitle {
    font-size: 1.1rem;
    color: #bdbdbd;
}

/* 2. Card Styling */
.card-style {
    background-color: white;
    padding: 25px;
    margin-bottom: 25px;
    border-radius: 12px; /* Smoother corners for elegance */
    box-shadow: 0 6px 15px rgba(0, 0, 0, 0.08);
    transition: box-shadow 0.3s;
}

.card-style:hover {
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.12);
}

.section-title {
    font-family: var(--font-serif);
    color: var(--color-maroon);
    border-bottom: 2px solid var(--color-gold); /* Rich accent line */
    padding-bottom: 10px;
    margin-bottom: 20px;
}

/* 3. Profile Section Styling */
.stylist-profile-section {
    display: flex;
    align-items: center;
    gap: 30px;
    border-left: 5px solid var(--color-maroon); /* Strong accent border */
}

.stylist-picture-placeholder {
    width: 90px;
    height: 90px;
    border-radius: 50%;
    background-color: var(--color-earth); /* Warm Earth Tone */
    border: 3px solid var(--color-gold); /* Gold ring */
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 3rem;
    color: white;
    font-weight: bold;
    flex-shrink: 0;
    box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
}

.stylist-name {
    font-size: 2rem;
    color: var(--color-dark);
    margin-bottom: 5px;
    font-weight: 700;
}

.stylist-email, .stylist-status {
    color: #555;
    font-size: 1rem;
    margin-bottom: 3px;
}

.stylist-specialty {
    font-size: 1rem;
    color: var(--color-maroon); /* Specialty in Maroon */
    margin-top: 8px;
    padding-top: 5px;
    border-top: 1px dashed #e0e0e0;
}

.status-tag {
    display: inline-block;
    padding: 4px 10px;
    margin-top: 5px;
    border-radius: 4px;
    background-color: #e6ffe6;
    color: #1a8f1a;
    font-weight: 600;
    font-size: 0.9rem;
}

/*** */
/* Appointment Card Specific Styling */
.appointment-card {
    border-left: 4px solid var(--color-gold) !important; /* Gold for appointments vs Sienna for slots */
    display: flex;
    flex-direction: column;
    align-items: flex-start !important;
    gap: 5px;
}

.appointment-main {
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.appointment-date {
    font-weight: bold;
    color: var(--color-maroon);
    font-size: 0.95rem;
}

.appointment-service {
    font-family: var(--font-serif);
    color: var(--color-dark);
    font-style: italic;
}

.appointment-meta {
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 5px;
    padding-top: 5px;
    border-top: 1px solid rgba(0,0,0,0.05);
}

.client-tag {
    font-size: 0.85rem;
    color: var(--color-earth);
}

/* Status Indicator Dots */
.status-dot {
    height: 8px;
    width: 8px;
    border-radius: 50%;
    display: inline-block;
}
.status-dot.confirmed { background-color: #1a8f1a; }
.status-dot.pending { background-color: var(--color-gold); }

/* Ensure the Scrollable areas feel balanced */
.summary-card {
    border-top: 4px solid var(--color-maroon);
}

/* 4. Timeslot Selection Section (Main Grid) */
.dashboard-grid {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 10px;
    width: 98%
    /*grid-template-columns: 1fr 2fr; */
    
}
.choose-time-slot {
    max-width: 800px;
    width: 100%;
    margin-left: 0;
    margin: 0 auto;

    text-align: center;
}
.available-services h4, .choose-time-slot h4 {
    color: var(--color-earth); /* Earth tone for subheadings */
    font-family: var(--font-serif);
    font-size: 1.2rem;
    margin-bottom: 15px;
}

/* Service List Styling */
.service-list {
    border: 1px solid #e0e0e0;
    padding: 15px;
    border-radius: 8px;
    max-height: 400px;
    overflow-y: auto;
    background-color: var(--color-light); /* Light background for contrast */
}

.service-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 0;
    border-bottom: 1px dotted #ccc;
    font-size: 1rem;
}

.service-item:last-child {
    border-bottom: none;
}

.service-item strong {
    font-weight: 600;
    color: var(--color-dark);
}

.service-category {
    font-size: 0.8rem;
    color: var(--color-earth);
    margin-left: auto;
    padding-left: 10px;
}

.service-price {
    font-weight: bold;
    color: var(--color-maroon); /* Maroon for price */
}

.no-services-msg {
    text-align: center;
    color: #888;
    padding: 20px;
    font-style: italic;
}

/* Responsiveness */
@media (max-width: 768px) {
    .dashboard-grid {
        padding: 10px;
    }

    .timeslot-selection-section.card-style {
        padding: 15px;
        width: 100%;
        box-sizing: border-box;
    }

    .choose-time-slot {
        margin-left: 0;
    }
}

@media (max-width: 600px) {
    .stylist-profile-section {
        flex-direction: column;
        text-align: center;
        border-left: none; /* Remove side border on mobile */
        border-top: 5px solid var(--color-maroon);
    }
    .stylist-picture-placeholder {
        margin-bottom: 10px;
    }
    .stylist-specialty {
        border-top: 1px dashed #e0e0e0; /* Re-add border below specialties */
    }
    .welcome-title {
        font-size: 2.5rem;
    }
}
</style>
